import React, { useCallback, useEffect, useState } from "react";
import { Button, Empty, Spin, Table, Tag, message } from "antd";
import dayjs from "dayjs";

import { controller as rootController } from "../../controller";
import "./crm.css";

/**
 * Requests: suggestions raised in the app, accepted with one click.
 *
 * Lives as a tab under Carrier Knowledge because every request today is a
 * carrier_contact one, raised from the carrier contact bar by the FTC staff who
 * dial these numbers and find them stale — so this is where someone already
 * looking at carrier facts would expect it.
 *
 * ⚠️ PlatformRequest is deliberately generic; carrier_contact is only its first
 * Type. A second type is the signal to pull this back out into its own surface —
 * a non-carrier suggestion buried under Carrier Knowledge would never be found.
 *
 * Deliberately generic. The only request type today is a carrier's contact
 * details being wrong, reported from the insurance modal — but nothing on this
 * page knows what a carrier is. It renders whatever the request proposes as an
 * old → new diff, so a new request type needs no change here.
 *
 * Every endpoint behind it is IsSuperUser server-side; the menu item is
 * super-admin gated too, so there's no client-side guard.
 */

const TABS = [
  { key: "pending", label: "Pending" },
  { key: "accepted", label: "Accepted" },
  { key: "rejected", label: "Rejected" },
  { key: "all", label: "All" },
];

const TYPE_LABEL = { carrier_contact: "Carrier contact" };

const FIELD_LABEL = {
  phone_number: "Phone",
  portal_name: "Portal name",
  portal_url: "Portal URL",
  breakdown_method: "Breakdowns",
  estimate_method: "Estimates",
};

export default function RequestsPanel() {
  const [status, setStatus] = useState("pending");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async function loadInner(next) {
    setLoading(true);
    try {
      const res = await rootController.listPlatformRequests(next || status);
      setRows(Array.isArray(res.json) ? res.json : []);
    } catch (e) {
      console.error(e);
      message.error("Couldn't load requests.");
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(function () { load(); }, [status]); // eslint-disable-line react-hooks/exhaustive-deps

  async function review(row, action) {
    setBusyId(row.id);
    try {
      const res = await rootController.reviewPlatformRequest(row.id, action);
      if (res && res.status >= 200 && res.status < 300) {
        message.success(action === "accept" ? "Applied." : "Rejected.");
        load();
        return;
      }
      message.error(((res && res.json) || {}).detail || "Couldn't complete that.");
    } finally {
      setBusyId(null);
    }
  }

  const columns = [
    {
      title: "What",
      key: "what",
      width: "20%",
      render: function (_, r) {
        return (
          <div>
            <div style={{ fontWeight: 600, color: "#2b2440" }}>{r.target_label}</div>
            <div style={{ fontSize: 12.5, color: "#848696" }}>
              {TYPE_LABEL[r.request_type] || r.request_type}
            </div>
          </div>
        );
      },
    },
    {
      title: "Proposed change",
      key: "diff",
      // The whole job of this page is judging a diff, so old → new is the row,
      // not something behind a click.
      render: function (_, r) {
        const keys = Object.keys(r.payload || {});
        if (!keys.length) return <span style={{ color: "#848696" }}>—</span>;
        return (
          <div>
            {keys.map(function (k) {
              const was = (r.current_values || {})[k] || "";
              return (
                <div key={k} style={{ fontSize: 13, marginBottom: 2 }}>
                  <span style={{ color: "#848696" }}>{FIELD_LABEL[k] || k}: </span>
                  <span style={{ textDecoration: "line-through", color: "#9a96a8" }}>
                    {was || "(blank)"}
                  </span>
                  <span style={{ color: "#848696" }}> → </span>
                  <span style={{ color: "#2b2440", fontWeight: 600 }}>
                    {r.payload[k] || "(blank)"}
                  </span>
                </div>
              );
            })}
            {r.note ? (
              <div style={{ fontSize: 12.5, color: "#848696", marginTop: 4 }}>“{r.note}”</div>
            ) : null}
          </div>
        );
      },
    },
    {
      title: "From",
      key: "from",
      width: "16%",
      render: function (_, r) {
        return (
          <div style={{ fontSize: 13 }}>
            <div>{r.submitted_by_name || "—"}</div>
            <div style={{ color: "#848696", fontSize: 12 }}>
              {r.submitted_at ? dayjs(r.submitted_at).format("MMM D, YYYY") : ""}
            </div>
          </div>
        );
      },
    },
    {
      title: "",
      key: "actions",
      width: 210,
      align: "right",
      render: function (_, r) {
        if (r.status !== "pending") {
          return (
            <Tag color={r.status === "accepted" ? "green" : "default"}>
              {r.status === "accepted" ? "Applied" : "Rejected"}
            </Tag>
          );
        }
        return (
          <>
            <Button
              size="small"
              onClick={function () { review(r, "reject"); }}
              disabled={busyId === r.id}
              style={{ marginRight: 8 }}
            >
              Reject
            </Button>
            <Button
              size="small"
              type="primary"
              loading={busyId === r.id}
              onClick={function () { review(r, "accept"); }}
            >
              Accept
            </Button>
          </>
        );
      },
    },
  ];

  return (
    <>
      <p className="crm-sub" style={{ marginBottom: 12 }}>
        Suggestions raised in the app, awaiting review. Today these all come from
        the carrier contact bar, which is why they live here.
      </p>

      <div className="crm-requests-tabs">
        {TABS.map(function (t) {
          return (
            <button
              key={t.key}
              type="button"
              className={"crm-requests-tab" + (status === t.key ? " is-active" : "")}
              onClick={function () { setStatus(t.key); }}
            >
              {t.label}
            </button>
          );
        })}
      </div>

      {loading ? (
        <div style={{ padding: 40, textAlign: "center" }}><Spin /></div>
      ) : rows.length === 0 ? (
        <Empty
          style={{ padding: 40 }}
          description={status === "pending" ? "Nothing waiting for review" : "Nothing here"}
        />
      ) : (
        <Table
          rowKey="id"
          columns={columns}
          dataSource={rows}
          pagination={false}
          className="crm-table"
        />
      )}
    </>
  );
}
